import { useState, useEffect } from "react";

export default function Login({ onSuccess, onBack }) {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const err = params.get("error");
    if (err === "expired") setError("Your login link expired. Request a new one below.");
    if (err === "invalid") setError("Invalid login link. Request a new one below.");
  }, []);

  const handleSend = async () => {
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/auth-send-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = await res.json();
      
      if (data.success) {
        setSent(true);
      } else if (data.error === "no_subscription") {
        setError("No account found for that email. Pick a plan first, then come back to sign in.");
      } else if (data.error === "email_failed") {
        setError("We couldn't send the email right now. Please try again in a moment.");
      } else if (data.error === "stripe_error") {
        setError("Something went wrong. Please try again.");
      } else {
        setError("Something went wrong. Please try again.");
      }
    } catch {
      setError("Connection error. Please check your internet and try again.");
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f7f7f5", fontFamily: "Inter, sans-serif", display: "flex", flexDirection: "column" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input:focus { outline: none; border-color: #3b5bdb !important; box-shadow: 0 0 0 3px rgba(59,91,219,0.08); }
      `}</style>

      {/* Nav */}
      <div style={{ borderBottom: "1px solid #e8e8e4", padding: "14px 28px", background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18, fontWeight: 800, color: "#1a1a1a" }}>PDFTopicScout</span>
          <span style={{ fontSize: 12, color: "#3b5bdb", fontWeight: 600 }}>by Creatovate</span>
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
        <div style={{ width: "100%", maxWidth: 400 }}>
          {!sent ? (
            <div style={{ background: "#fff", borderRadius: 14, padding: 36, border: "1px solid #e8e8e4", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: "#1a1a1a", marginBottom: 6 }}>Sign in</h1>
              <p style={{ fontSize: 14, color: "#999", marginBottom: 28, lineHeight: 1.6 }}>
                Enter the email you used to subscribe. We'll send a magic link — no password needed.
              </p>

              <label style={{ fontSize: 12, color: "#999", fontWeight: 500, display: "block", marginBottom: 6 }}>Email address</label>
              <input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === "Enter" && !loading && handleSend()}
                style={{
                  width: "100%", background: "#fff", border: "1px solid #e8e8e4",
                  borderRadius: 8, padding: "10px 14px", fontSize: 14, color: "#1a1a1a",
                  marginBottom: 14, transition: "border-color 0.15s",
                }}
              />

              {error && (
                <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "#dc2626", marginBottom: 14, lineHeight: 1.5 }}>
                  {error}
                </div>
              )}

              <button
                onClick={handleSend}
                disabled={loading}
                style={{
                  width: "100%", background: loading ? "#c5cff7" : "#3b5bdb",
                  color: "#fff", border: "none", borderRadius: 8, padding: 12,
                  fontWeight: 700, fontSize: 14, cursor: loading ? "not-allowed" : "pointer",
                  fontFamily: "Inter, sans-serif", transition: "background 0.2s",
                }}
              >
                {loading ? "Sending…" : "Send magic link →"}
              </button>

              <p style={{ textAlign: "center", marginTop: 20, fontSize: 13, color: "#aaa" }}>
                Don't have an account?{" "}
                <button
                  onClick={onBack}
                  style={{ background: "none", border: "none", color: "#3b5bdb", fontWeight: 600, cursor: "pointer", fontSize: 13 }}
                >
                  See plans
                </button>
              </p>
            </div>
          ) : (
            <div style={{ background: "#fff", borderRadius: 14, padding: 36, border: "1px solid #e8e8e4", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📧</div>
              <h1 style={{ fontSize: 22, fontWeight: 800, color: "#1a1a1a", marginBottom: 8 }}>Check your inbox</h1>
              <p style={{ fontSize: 14, color: "#666", lineHeight: 1.7, marginBottom: 8 }}>
                We sent a magic link to
              </p>
              <p style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a", marginBottom: 20 }}>{email}</p>
              <p style={{ fontSize: 13, color: "#999", lineHeight: 1.6, marginBottom: 24 }}>
                Click the link in the email to sign in. It expires in 15 minutes. Check your spam folder if you don't see it.
              </p>
              <button
                onClick={() => { setSent(false); setError(""); }}
                style={{ background: "none", border: "1px solid #e8e8e4", color: "#666", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontFamily: "Inter, sans-serif" }}
              >
                Try a different email
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: "1px solid #e8e8e4", padding: "16px 28px", background: "#fff", textAlign: "center" }}>
        <span style={{ fontSize: 12, color: "#aaa" }}>
          <strong style={{ color: "#1a1a1a" }}>PDFTopicScout</strong> by{" "}
          <strong style={{ color: "#3b5bdb" }}>Creatovate</strong>
          {" — "}
          <a href="mailto:info@creatovate.biz" style={{ color: "#aaa", textDecoration: "none" }}>info@creatovate.biz</a>
        </span>
      </div>
    </div>
  );
}
